import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../utils/apiConfig';
import VerkaufsangeboteManagement from '../components/VerkaufsangeboteManagement';

const VerkaeuferLoginPage = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('login');
  const [name, setName] = useState('');
  const [firma, setFirma] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [sellerUser, setSellerUser] = useState(null);

  useEffect(() => {
    const loadSession = async () => {
      try {
        const response = await axios.get(getApiUrl('/api/public/seller/session'));
        setSellerUser(response.data.user || null);
      } catch (err) {
        setSellerUser(null);
      } finally {
        setCheckingSession(false);
      }
    };

    loadSession();
  }, []);

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoggingIn(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(getApiUrl('/api/public/seller/login'), {
        email: email.trim(),
        password,
      });
      setSellerUser(response.data.user || null);
      setSuccess('Verkäuferzugang aktiv.');
    } catch (err) {
      setError(err?.response?.data?.message || 'Login fehlgeschlagen.');
    } finally {
      setLoggingIn(false);
    }
  };

  const handleRegister = async (event) => {
    event.preventDefault();
    setRegistering(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(getApiUrl('/api/public/seller/register'), {
        name: name.trim(),
        email: email.trim(),
        password,
        firma: firma.trim(),
      });
      setSellerUser(response.data.user || null);
      setSuccess('Verkäuferkonto erstellt und angemeldet.');
    } catch (err) {
      setError(err?.response?.data?.message || 'Registrierung fehlgeschlagen.');
    } finally {
      setRegistering(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(getApiUrl('/api/public/seller/logout'));
    } catch (err) {
      // ignore logout transport errors
    }
    setSellerUser(null);
    setSuccess('Abgemeldet.');
  };

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', py: { xs: 4, md: 6 } }}>
      <Container maxWidth="md">
        <Stack spacing={2.5}>
          <Button onClick={() => navigate('/')} sx={{ width: 'fit-content', textTransform: 'none', fontWeight: 700 }}>
            Zur Hauptseite
          </Button>

          <Card sx={{ borderRadius: 3, border: '1px solid #dbe3ee' }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#1a365d', mb: 1 }}>
                Verkäuferzugang
              </Typography>
              <Typography variant="body1" sx={{ color: '#64748b', mb: 2.5 }}>
                Verkäufer- und Vermieterkonten nutzen dieselbe Userbasis wie die Hausverwaltung, aber mit eigenem Kontotyp für die Verkaufs- und Mietverwaltung.
              </Typography>

              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

              {checkingSession && (
                <Box sx={{ display: 'grid', placeItems: 'center', py: 3 }}>
                  <CircularProgress />
                </Box>
              )}

              {!checkingSession && !sellerUser && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
                    <ToggleButtonGroup
                      exclusive
                      value={mode}
                      onChange={(_, value) => value && setMode(value)}
                      size="small"
                    >
                      <ToggleButton value="login">Login</ToggleButton>
                      <ToggleButton value="register">Registrieren</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>

                  <Box component="form" onSubmit={mode === 'login' ? handleLogin : handleRegister}>
                    <Stack spacing={2}>
                      {mode === 'register' && (
                        <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} required fullWidth />
                      )}
                      {mode === 'register' && (
                        <TextField label="Firma (optional)" value={firma} onChange={(e) => setFirma(e.target.value)} fullWidth />
                      )}
                      <TextField label="E-Mail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required fullWidth />
                      <TextField label="Passwort" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required fullWidth />
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={loggingIn || registering}
                        sx={{ textTransform: 'none', fontWeight: 700 }}
                      >
                        {mode === 'login'
                          ? (loggingIn ? 'Login läuft...' : 'Einloggen')
                          : (registering ? 'Registrierung läuft...' : 'Konto erstellen')}
                      </Button>
                    </Stack>
                  </Box>
                </>
              )}

              {!checkingSession && sellerUser && (
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1.5}>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>
                        Verkaufs- und Mietverwaltung
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#64748b' }}>
                        Angemeldet als {sellerUser.email}
                      </Typography>
                    </Box>
                    <Button variant="text" color="error" onClick={handleLogout} sx={{ textTransform: 'none', fontWeight: 700 }}>
                      Ausloggen
                    </Button>
                  </Stack>

                  <VerkaufsangeboteManagement />
                </Stack>
              )}
            </CardContent>
          </Card>
        </Stack>
      </Container>
    </Box>
  );
};

export default VerkaeuferLoginPage;
